/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, OnInit, Optional, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderPermission, ContactType } from '@gh/builder-shared-data';
import {
  AppRouter,
  ContainerStateDirective,
  getAllNewSelectedEntities,
  getRouteParam,
} from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { translationTuple } from '@gh/core-mls';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import { openInNewWindow, toString } from '@gh/core-util';
import { filter$, map$ } from '@gh/rx/operators';

import { JobBasicInfo, JobDrawDetailsTabBusinessService } from '../../resources/job-details-tab';
import { JobDetailsModel } from '../../services/jobt-details-model.service';
import {
  isJobEditable,
  jobCsCreateDialogRoute, jobSpecCreateDialogRoute,
} from '../../';

import { BuilderUserContext } from '../../../user';
import {
  ContactFinderDialogConfig,
  contactFinderDialogRoute,
  ContactFinderListItem,
} from '../../../';
import {
  ComponentJobDetailsReassignConfig,
  componentReassignJobDialogRoute,
} from '../job-details-reassign-dialog/job-details-reassign-dialog.component';

@Component({
  selector: 'gh-job-details-toolbar-component',
  template: require('./job-details-toolbar.component.html'),
  styles: [require('./job-details-toolbar.component.scss')],
})
export class JobDetailToolbarComponent implements OnInit {
  @Output() print = new EventEmitter();
  @Output() reassign = new EventEmitter();

  basicInfo: JobBasicInfo;
  jobId: ID;

  reportUrl: string;

  constructor(private userContext: BuilderUserContext,
              private dialogs: DialogService,
              private router: AppRouter,
              private model: JobDetailsModel,
              private route: ActivatedRoute,
              private notificationService: NotificationService,
              private jobDetailsService: JobDrawDetailsTabBusinessService,
              private waitings: WaitingService,
              @Optional() private containerState?: ContainerStateDirective) {
  }

  get editMode(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
      && isJobEditable(this.model.basicInfo);
  }

  get jobSpecificationAllow(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT) ||
      this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.VIEW);
  }

  get jobColorSelectionAllow(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT) ||
      this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.VIEW);
  }

  get jobFinancialsAllow(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.Financials.VIEW);
  }

  get reassignAllow(): boolean {
    return this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.REASSIGN)
      && isJobEditable(this.model.basicInfo);
  }

  ngOnInit(): void {
    this.jobId = getRouteParam(this.route.parent!, 'jobId'); // tslint:disable-line:no-non-null-assertion
    this.basicInfo = this.model.basicInfo;
    this.reportUrl = this.model.reportUrl;
  }

  onJobSpecClick(): void {
    const { jobId, jobNumber, isSpecExist } = this.basicInfo;

    if (this.containerState && this.containerState.containerState.dirty) {
      this.notificationService.error('msg_save_first');
    } else if (isSpecExist) {
      this.router.navigateToChild([`/job-admin/jobs/${jobId}/spec`], {
        parentTitle: translationTuple('lbl_job_number_with_param', { number: toString(jobNumber) }),
      });
    } else if (!isJobEditable(this.basicInfo)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobSpecifications.CREATE_EDIT)) {
      this.notificationService.error('msg_spec_empty');
    } else {
      this.dialogs.openWithInput(jobSpecCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((formValue: any) => {
          this.router.navigateToChild(
            [`/job-admin/jobs/${jobId}/spec/new`],
            {
              queryParams: { specTemplateId: formValue.jobSpecTemplate.id },
              parentTitle: translationTuple('lbl_job_number_with_param', { number: toString(jobNumber) }),
            });
        });
    }
  }

  onJobColorSelectionClick(): void {
    const { jobId, jobNumber, isCsExist } = this.basicInfo;

    if (this.containerState && this.containerState.containerState.dirty) {
      this.notificationService.error('msg_save_first');
    } else if (isCsExist) {
      this.router.navigateToChild(['job-admin/jobs', jobId, 'color-selection'], {
        parentTitle: translationTuple('lbl_job_number_with_param', { number: toString(jobNumber) }),
      });
    } else if (!isJobEditable(this.basicInfo)
      || !this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT)) {
      this.notificationService.error('msg_cs_empty');
    } else {
      this.dialogs.openWithInput(jobCsCreateDialogRoute, {isFromCreateBtn: false})
        .afterClosed().pipe(
        filter$(Boolean))
        .subscribe((csm: any) =>
          this.router.navigateToChild(
            ['job-admin/jobs', jobId, 'color-selection', 'new'],
            {
              queryParams: { csMasterId: csm.master.id },
              parentTitle: translationTuple('lbl_job_number_with_param', { number: toString(jobNumber) }),
            }));
    }
  }

  onJobFinancialsClick(): void {
    const { jobId } = this.basicInfo;
    if (this.containerState && this.containerState.containerState.dirty) {
      this.notificationService.error('msg_save_first');
    } else {
      this.router.navigateToChild(['job-admin', 'job-financials', jobId], {
      });
    }
  }

  onChangeClientClick(): void {
    if (this.containerState && this.containerState.containerState.dirty) {
      this.notificationService.error('msg_save_first');
    } else {
      this.dialogs.openWithInput(contactFinderDialogRoute, <ContactFinderDialogConfig>{
        type: ContactType.Customer,
        withAddress: true,
        multiselection: false,
        selection: true,
      }).afterClosed().pipe(
        filter$(Boolean),
        map$(getAllNewSelectedEntities))
        .subscribe((contacts: ContactFinderListItem[]) => {
          this.jobDetailsService.changeClient(
            this.basicInfo.jobId,
            this.basicInfo.version,
            contacts[0].id,
            this.basicInfo.contactId).pipe(this.waitings.operator()).subscribe(
            (basicInfo) => this.model.updateBasicInfo(basicInfo),
            this.notificationService.processAll);
      }, this.notificationService.processAll); // tslint:disable-line:align
    }
  }

  printJob(): void {
    openInNewWindow(this.reportUrl);
  }

  private onReassignClick(): void {
    const { jobId, version } = this.basicInfo;

    if (this.containerState && this.containerState.containerState.dirty) {
      this.notificationService.error('msg_save_first');
    } else {
      this.dialogs.openWithInput(
        componentReassignJobDialogRoute,
        <ComponentJobDetailsReassignConfig> {
          jobId,
          version,
        }).afterClosed()
        .subscribe((res) => {
          if (res) {
            this.notificationService.success('msg_saved_successfully');
            this.jobDetailsService.getBasicInfo().pipe(this.waitings.operator()).subscribe(
              (basicInfo) => {
                this.model.updateBasicInfo(basicInfo);
                this.basicInfo.version = basicInfo.version;
              },
              this.notificationService.processAll);
            this.reassign.emit();
          }
        },         this.notificationService.processAll);
    }
  }
}
